import { FC, useCallback } from "react";
import { PopoverProps } from "@mui/material";
import { Dates } from "@/services/cloudchipr.api";
import { CostAnomaliesWidgetCardHeaderDate } from "@/components/pages/dashboard/components/widgets/cost-anomalies/components/header/CostAnomaliesWidgetCardHeaderDate";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { costAnomaliesWidgetPrevPeriodCostDateDetailsSelector } from "@/store/dashboards/selectors/widgets/cost-anomalies/widget-data/costAnomaliesWidgetPrevPeriodCostDateDetailsSelector";
import { DateRangePicker } from "@/components/common/date-range-picker/DateRangePicker";
import { WidgetQuickActionTrigger } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/WidgetQuickActionTrigger";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { allDateRangesByQuarterStartMonthSelector } from "@/store/common/selectors/date-ranges/allDateRangesByQuarterStartMonthSelector";
import { updateCostAnomaliesWidgetFromQuickActionsThunk } from "@/store/dashboards/thunks/widgets/cost-anomalies/actions/updateCostAnomaliesWidgetFromQuickActionsThunk";
import { useAppAbility } from "@/services/permissions";
import { widgetInheritsDatesByIdSelector } from "@/store/dashboards/selectors/widgets/common/widgetInheritsDatesByIdSelector";
import { WidgetHeaderDateRangeFooter } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/date-range/WidgetHeaderDateRangeFooter";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { currentDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/currentDashboardIdSelector";
import { dashboardDatesByDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/filters/dashboardDatesByDashboardIdSelector";

interface CostAnomaliesWidgetHeaderDateSelectProps {
  dates?: Dates;
  widgetId: string;
}

export const CostAnomaliesWidgetHeaderDateSelect: FC<
  CostAnomaliesWidgetHeaderDateSelectProps
> = ({ widgetId, dates }) => {
  const dispatch = useAppDispatch();
  const prevDate = useAppSelector((state) =>
    costAnomaliesWidgetPrevPeriodCostDateDetailsSelector(state, widgetId),
  );
  const { cannot } = useAppAbility();
  const canNotEditWidget = cannot("edit", "dashboard");
  const isProtected = useAppSelector(currentDashboardProtectedSelector);

  const dateRanges = useAppSelector(allDateRangesByQuarterStartMonthSelector);
  const orgQuarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );
  const inheritDates = useAppSelector((state) =>
    widgetInheritsDatesByIdSelector(state, widgetId),
  );
  const dashboardId = useAppSelector(currentDashboardIdSelector);
  const dashboardDates = useAppSelector((state) =>
    dashboardDatesByDashboardIdSelector(state, dashboardId ?? ""),
  );

  const dateRangeChangeHandler = useCallback(
    (dates: Dates) => {
      dispatch(
        updateCostAnomaliesWidgetFromQuickActionsThunk({
          id: widgetId,
          dates,
        }),
      );
    },
    [dispatch, widgetId],
  );

  const syncWithDashboardHandler = useCallback(
    (checked: boolean) => {
      dispatch(
        updateCostAnomaliesWidgetFromQuickActionsThunk({
          id: widgetId,
          inherit_dates: checked,
          dates: dashboardDates ?? undefined,
        }),
      );
    },
    [dispatch, widgetId, dashboardDates],
  );

  if (inheritDates) {
    return null;
  }

  return (
    <CostAnomaliesWidgetCardHeaderDate prevDate={prevDate} date={dates}>
      {dates && !canNotEditWidget && !isProtected && (
        <DateRangePicker
          fullWidth
          highlightToday
          ranges={dateRanges}
          selectedDates={dates}
          PopoverProps={popoverProps}
          onDateSelect={dateRangeChangeHandler}
          quarterStartMonth={orgQuarterStartMonth}
          CustomTrigger={WidgetQuickActionTrigger}
          PopoverFooter={
            <WidgetHeaderDateRangeFooter
              inheritDates={inheritDates}
              onChange={syncWithDashboardHandler}
            />
          }
        />
      )}
    </CostAnomaliesWidgetCardHeaderDate>
  );
};

const popoverProps: Partial<PopoverProps> = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "right",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "right",
  },
};
